// Import Papa Parse
import Papa from 'papaparse'
import { uploadFilesToS3, validateS3Config } from './s3Upload.js'

// Utility for async processing that doesn't block the browser
export const processInChunks = async (items, processor, chunkSize = 100, onProgress = null) => {
  const results = []
  
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize)
    
    // Process chunk
    const chunkResults = chunk.map(processor)
    results.push(...chunkResults)
    
    // Yield control back to browser
    await new Promise(resolve => setTimeout(resolve, 0))
    
    // Update progress
    if (onProgress) {
      onProgress({
        processed: Math.min(i + chunkSize, items.length),
        total: items.length,
        percentage: Math.round(((i + chunkSize) / items.length) * 100)
      })
    }
  }
  
  return results
}

export const processFilesAsync = async (files, onFileProgress = null, onDataProgress = null) => {
  const allData = []
  const allLeaks = []
  let uploadResults = []
  
  // Check S3 configuration
  const s3Validation = validateS3Config()
  const s3Enabled = s3Validation.isValid
  
  if (!s3Enabled) {
    console.warn('S3 upload disabled:', s3Validation.errors.join(', '))
  }
  
  // Upload files to S3 first (if enabled)
  if (s3Enabled) {
    if (onFileProgress) {
      onFileProgress({
        current: 0,
        total: files.length,
        currentFile: 'Uploading files to S3...',
        stage: 'upload'
      })
    }
    
    try {
      uploadResults = await uploadFilesToS3(files, (progress) => {
        if (onFileProgress) {
          onFileProgress({
            current: progress.fileIndex || 0,
            total: progress.totalFiles || files.length,
            currentFile: progress.currentFile || 'Uploading...',
            stage: 'upload',
            uploadProgress: progress
          })
        }
      })
      
      console.log('S3 upload results:', uploadResults)
    } catch (error) {
      console.error('S3 upload failed:', error)
      // Continue processing even if upload fails
    }
  }
  
  // Process files for data analysis
  for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
    const file = files[fileIndex]
    
    if (onFileProgress) {
      onFileProgress({
        current: fileIndex + 1,
        total: files.length,
        currentFile: file.name,
        stage: 'processing'
      })
    }
    
    // Process CSV file
    const csvData = await processCSVDataAsync(file, onDataProgress)
    
    // Add file source to each data point
    const dataWithSource = csvData.map(point => ({
      ...point,
      sourceFile: file.name
    }))
    
    allData.push(...dataWithSource)
    
    // Yield control between files
    await new Promise(resolve => setTimeout(resolve, 10))
  }
  
  // Process leaks detection in chunks
  if (onDataProgress) {
    onDataProgress({
      processed: allData.length,
      total: allData.length,
      percentage: 100,
      stage: 'Detecting leaks...'
    })
  }
  
  const detectedLeaks = await detectLeaksAsync(allData, onDataProgress)
  
  return { 
    data: allData, 
    leaks: detectedLeaks,
    uploadResults: uploadResults,
    s3Enabled: s3Enabled
  }
}

// Async CSV processing
const processCSVDataAsync = async (file, onProgress = null) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      transformHeader: (header) => header.trim(),
      transform: (value) => value.trim(),
      complete: async (results) => {
        if (onProgress) {
          onProgress({
            processed: results.data.length,
            total: results.data.length,
            percentage: 50,
            stage: `Processing ${file.name}...`
          })
        }
        
        // Process data in chunks to avoid blocking
        const processedData = []
        const chunkSize = 1000
        
        for (let i = 0; i < results.data.length; i += chunkSize) {
          const chunk = results.data.slice(i, i + chunkSize)
          
          const processedChunk = chunk.map((row, index) => {
            try {
              return parseRow(row, i + index)
            } catch (error) {
              console.warn(`Error parsing row ${i + index}:`, error)
              return null
            }
          }).filter(Boolean)
          
          processedData.push(...processedChunk)
          
          // Yield control
          await new Promise(resolve => setTimeout(resolve, 0))
          
          // Update progress
          if (onProgress) {
            onProgress({
              processed: Math.min(i + chunkSize, results.data.length),
              total: results.data.length,
              percentage: Math.round(((i + chunkSize) / results.data.length) * 50) + 50,
              stage: `Processing ${file.name}...`
            })
          }
        }
        
        // Re-assign IDs
        const finalData = processedData.map((point, index) => ({
          ...point,
          id: index
        }))
        
        resolve(finalData)
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`))
      }
    })
  })
}

// Async leak detection
const detectLeaksAsync = async (data, onProgress = null) => {
  const leaks = []
  const chunkSize = 1000
  
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize)
    
    // Process chunk for leaks
    for (const point of chunk) {
      if (isPotentialLeak(point)) {
        leaks.push(point)
      }
    }
    
    // Yield control
    await new Promise(resolve => setTimeout(resolve, 0))
    
    // Update progress
    if (onProgress) {
      onProgress({
        processed: Math.min(i + chunkSize, data.length),
        total: data.length,
        percentage: Math.round(((i + chunkSize) / data.length) * 100),
        stage: 'Detecting leaks...'
      })
    }
  }
  
  // Cluster leaks asynchronously
  return clusterLeaksAsync(leaks, onProgress)
}

// Helper functions (imported from leakDetection.js)
const FALCON_MEAS_THRESHOLD = 500
const FALCON_1F_THRESHOLD = 30
const FALCON_2F_THRESHOLD = 100
const FALCON_STATE_OK = new Set(['0', '1'])

const isPotentialLeak = (point) => {
  if (point.falconMeas > FALCON_MEAS_THRESHOLD &&
      FALCON_STATE_OK.has(point.falconState) &&
      point.falcon1F > FALCON_1F_THRESHOLD &&
      point.lat !== 0 && point.lng !== 0) {
    
    if (point.falconMeas <= 200) {
      return point.falcon2F > FALCON_2F_THRESHOLD
    } else {
      return point.falcon2F > 0
    }
  }
  return false
}

const clusterLeaksAsync = async (leaks, onProgress = null) => {
  if (leaks.length === 0) return []
  
  const clusters = []
  const used = new Set()
  const CLUSTER_RADIUS_METERS = 30
  
  for (let i = 0; i < leaks.length; i++) {
    if (used.has(i)) continue
    
    const cluster = [leaks[i]]
    used.add(i)
    
    for (let j = 0; j < leaks.length; j++) {
      if (used.has(j)) continue
      
      const distance = haversineDistance(
        leaks[i].lat, leaks[i].lng,
        leaks[j].lat, leaks[j].lng
      )
      
      if (distance <= CLUSTER_RADIUS_METERS) {
        cluster.push(leaks[j])
        used.add(j)
      }
    }
    
    const bestLeak = cluster.reduce((best, current) => 
      current.falconMeas > best.falconMeas ? current : best
    )
    
    clusters.push({
      ...bestLeak,
      clusterSize: cluster.length,
      clusterMembers: cluster
    })
    
    // Yield control every 10 clusters
    if (i % 10 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0))
      
      if (onProgress) {
        onProgress({
          processed: i,
          total: leaks.length,
          percentage: Math.round((i / leaks.length) * 100),
          stage: 'Clustering leaks...'
        })
      }
    }
  }
  
  return clusters
}

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000
  const toRadians = (degrees) => degrees * (Math.PI / 180)
  
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)
  
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon/2) * Math.sin(dLon/2)
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  
  return R * c
}

// Parse row function (copied from csvParser.js)
const parseRow = (row, index) => {
  const get = (col) => {
    const value = row[col]
    return value !== undefined && value !== null && value !== '' ? String(value).trim() : ''
  }
  
  const lat = parseFloat(get('Lat') || get('GPS_LAT') || 0)
  const lng = parseFloat(get('Long') || get('GPS_LON') || 0)
  const alt = parseFloat(get('Alt') || get('GPS_ALT') || 0)
  
  if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
    return null
  }
  
  const parseFloatSafe = (value, defaultValue = 0) => {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? defaultValue : parsed
  }
  
  const parseIntSafe = (value, defaultValue = 0) => {
    const parsed = parseInt(value)
    return isNaN(parsed) ? defaultValue : parsed
  }
  
  return {
    id: index,
    timestamp: get('GPS_DATETIME') || `${get('DATE')}T${get('TIME')}`,
    falconMeas: parseFloatSafe(get('FALCON_MEAS')),
    falconState: get('FALCON_STATE'),
    falcon1F: parseFloatSafe(get('FALCON_1F')),
    falcon2F: parseFloatSafe(get('FALCON_2F')),
    lat,
    lng,
    alt,
    gpsSatnum: parseIntSafe(get('GPS_SATNUM')),
    gpsSpd: parseFloatSafe(get('GPS_SPD')),
    falconCnt: parseIntSafe(get('FALCON_CNT')),
    falconAlarm: parseIntSafe(get('FALCON_ALARM'))
  }
}
